import { useState } from "react";
import { AUDIENCES, C, rivals } from "@bd/content";
import { audienceSize, BALANCE, dateOf, heat, saturation, themeReleasesLastYear, trendBoard, yearOf, charts, fanBoard, league, money, rivalActive, rivalStudio } from "@bd/sim";
import { Icon, Meter, Sheet, Tabs, Why } from "@bd/ui-kit";
import { me, txLine, useGame } from "../store";
import { ScoreBadge } from "./Reviews";
import { Clock } from "./Research";
import { t, tp } from "../i18n";

/** A column head as an icon, its word on hover. */
function Th({ icon, label, className }: { icon: string; label: string; className?: string }) {
  return <th className={className} title={label} aria-label={label}><span className="biz-th">{icon === "clock" ? <Clock size={14} /> : <Icon name={icon} size={14} />}</span></th>;
}

export function Market() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const co = me(game)!;
  const screen = useGame((s) => s.screen);
  const [tab, setTab] = useState<"trends" | "league" | "charts" | "fans" | "audiences" | "rivals" | "news" | "awards" | "notebook">(screen?.kind === "market" && screen.tab ? screen.tab : "trends");
  const table = league(game);
  // "14 people · studio": your size, or a rival's (worked out from how they're doing)
  const studioLine = (id: string) => {
    const rs = id === co.id ? null : game.rivals.find((x) => x.id === id);
    if (id !== co.id && !rs) return null;
    const st = rs ? rivalStudio(rs, game.week) : { staff: co.staff.length, level: co.level };
    return <span className="biz-ic" title={`${tp(st.staff, "{n} person", "{n} people")} · ${t(C.level[st.level].name)}`}><Icon name="staff" size={14} />{st.staff} · {t(C.level[st.level].name).toLowerCase()}</span>;
  };
  const chart = charts(game);
  const board = trendBoard(game).slice(0, 14);
  const year = yearOf(game.week);
  const recent = [...game.market.games].filter((g) => game.week - g.week < 26).sort((a, b) => b.week - a.week).slice(0, 30);
  return (
    <Sheet title={t("Market")} onClose={() => set({ screen: null })} drawer testId="market">
      <div className="biz-tabs"><Tabs tabs={[{ id: "trends", label: t("Trends") }, { id: "league", label: t("League") }, { id: "charts", label: t("Charts") }, { id: "fans", label: t("Fan Board") }, { id: "audiences", label: t("Audiences") }, { id: "rivals", label: t("Rivals") }, { id: "news", label: t("News") }, { id: "awards", label: t("Awards") }, { id: "notebook", label: t("Notebook") }]} value={tab} onChange={setTab} /></div>
      <div style={{ marginTop: 14 }}>
        {tab === "trends" && (
          <div className="grid2">
            <div className="stack">
              <span className="caps">{t("What's hot")} <Why title={t("Why trends matter")} lines={[{ label: t("Reviewers reward good timing"), value: "" }, { label: t("Hot themes sell more"), value: t("up to +60%") }, { label: t("Crowded themes sell less"), value: t("−5% a game") }]} note={t("Crowded: over three games in one theme in a year.")} /></span>
              {board.length === 0 && <span className="muted">{t("Nothing is trending right now.")}</span>}
              {board.map((w) => (
                <div key={w.kind + w.target} className="row" style={{ gap: 10 }}>
                  <span style={{ width: 170 }} className="ellipsis" title={`${t(w.name)} · ${t(w.kind)}`}>{t(w.name)}{w.kind === "mechanic" && <span className="muted" style={{ fontSize: "0.72rem" }}> {t("mech")}</span>}</span>
                  <Meter value={w.heat} color={w.rising ? "var(--coral)" : "var(--gold)"} style={{ flex: 1 }} label={t("{name} heat", { name: t(w.name) })} />
                  <span className="num" style={{ width: 70, textAlign: "right" }}>{Math.round(w.heat * 100)}% {w.rising ? "↑" : "↓"}</span>
                </div>
              ))}
            </div>
            <div className="stack">
              <span className="caps" title={t("Released, last six months")}>{t("Released")}</span>
              {recent.length === 0 && <span className="muted">{t("Nothing yet.")}</span>}
              <div className="biz-rows">
                {recent.map((g) => {
                  const combo = `${t(C.mechanic[g.mechanic].name)} × ${t(C.theme[g.theme].name)}`;
                  return (
                    <div key={g.id} className="biz-row biz-rel" title={combo}>
                      <span className="ellipsis">{g.name}</span>
                      <span className="ellipsis muted">{C.rival[g.publisher]?.name ?? game.companies.find((c) => c.id === g.publisher)?.name ?? "?"}</span>
                      <span className="ellipsis muted biz-hide-phone" style={{ fontSize: "0.82rem" }}>{combo}</span>
                      <span>{g.publisher === co.id || g.score >= 0 ? <ScoreBadge score={g.score} compact /> : null}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {tab === "audiences" && (
          <table className="list biz-compact">
            <thead><tr><th>{t("Audience")}</th><Th icon="fans" label={t("Weekly buyers")} /><Th icon="coin" label={t("Pays up to")} /><Th icon="clock" label={t("Plays for")} /></tr></thead>
            <tbody>
              {AUDIENCES.map((a) => {
                const def = C.audience[a];
                const size = audienceSize(a, game.week) * BALANCE.salesScale;
                return (
                  <tr key={a}>
                    <td><strong>{t(def.name)}</strong>{!co.unlocked.includes(`a:${a}`) && <span className="muted biz-ic" style={{ marginLeft: 6 }} title={t("Opens {year}", { year: def.year })}><Icon name="lock" size={13} />{def.year}</span>}</td>
                    <td><span className="row"><span className="biz-hide-phone"><Meter value={size / 12000} color="var(--teal)" style={{ width: 120 }} /></span> <span className="num">{Math.round(size).toLocaleString("en-US")}</span></span></td>
                    <td className="num">${(def.priceCeiling * Math.pow(1.028, year - 1965)).toFixed(0)}</td>
                    <td className="num">{t("{n} min", { n: def.playTimeLimit })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {tab === "league" && (
          <table className="list biz-compact" data-testid="league">
            <thead><tr><th>#</th><th>{t("Publisher")}</th><th className="biz-hide-phone">{t("Studio")}</th><Th icon="coin" label={t("Sales this year")} /><Th icon="fans" label={t("Fans")} /><Th icon="trophy" label={t("Awards")} /></tr></thead>
            <tbody>
              {table.map((r) => (
                <tr key={r.id} style={r.id === co.id ? { background: "rgba(232, 88, 72, 0.08)" } : undefined}>
                  <td className="num">{r.rank}</td>
                  <td><strong>{r.name}</strong></td>
                  <td className="muted biz-hide-phone" style={{ fontSize: "0.85rem" }}>{studioLine(r.id)}</td>
                  <td className="num">{money(r.revenue)}</td>
                  <td className="num">{r.fans.toLocaleString("en-US")}</td>
                  <td className="num">{r.awards}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "fans" && (
          <div className="stack" style={{ gap: 6 }}>
            <table className="list biz-compact" data-testid="fan-board">
              <thead><tr><th>#</th><th>{t("Game")}</th><th>{t("Publisher")}</th><th className="biz-hide-phone">{t("Year")}</th><th title={t("Fan rating")}><span className="biz-th"><Icon name="star" size={14} /><Why title={t("Fan rating")} lines={[{ label: t("Reviews set the start"), value: "" }, { label: t("Being played keeps it up"), value: "" }, { label: t("Age wears it down"), value: t("evergreens barely") }]} /></span></th></tr></thead>
              <tbody>
                {fanBoard(game).map((g, i) => (
                  <tr key={g.id} style={g.yours === co.id ? { background: "rgba(232, 88, 72, 0.08)" } : undefined}>
                    <td className="num">{i + 1}</td>
                    <td><strong>{g.name}</strong></td>
                    <td className="muted">{g.publisher}</td>
                    <td className="num biz-hide-phone">{g.year}</td>
                    <td className="num">{g.rating.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "charts" && (
          <table className="list biz-compact" data-testid="charts">
            <thead><tr><th>#</th><th>{t("Game")}</th><th>{t("Publisher")}</th><Th icon="star" label={t("Score")} /><Th icon="box" label={t("Copies")} /></tr></thead>
            <tbody>
              {chart.map((c, i) => (
                <tr key={c.game.id} style={c.yours ? { background: "rgba(232, 88, 72, 0.08)" } : undefined}>
                  <td className="num">{i + 1}</td>
                  <td><strong>{c.game.name}</strong></td>
                  <td className="muted">{c.publisher}</td>
                  <td><ScoreBadge score={c.game.score} compact /></td>
                  <td className="num">{c.sold.toLocaleString("en-US")}</td>
                </tr>
              ))}
              {chart.length === 0 && <tr><td colSpan={5} className="muted">{t("No new games this year yet.")}</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "rivals" && (
          <table className="list biz-compact">
            <thead><tr><th>{t("Publisher")}</th><th className="biz-hide-phone">{t("Style")}</th><th className="biz-hide-phone">{t("Studio")}</th><Th icon="box" label={t("Games")} /><th /></tr></thead>
            <tbody>
              {rivals.filter((r) => !r.upstart || game.rivals.find((x) => x.id === r.id)?.foundedWeek !== undefined).map((r) => {
                const rs = game.rivals.find((x) => x.id === r.id)!;
                const active = rivalActive(rs, r, game.week);
                return (
                  <tr key={r.id}>
                    <td title={`${t(r.personality)} · ${t("Will try to")}: ${t(r.will)}`}><span className="row"><span style={{ width: 10, height: 10, borderRadius: 5, background: r.color, display: "inline-block", flex: "none" }} /><strong>{r.name}</strong></span></td>
                    <td className="muted biz-hide-phone" title={t(r.will)}>{t(r.personality)}</td>
                    <td className="muted biz-hide-phone" style={{ fontSize: "0.85rem" }}>{active ? studioLine(r.id) : "—"}</td>
                    <td className="num">{rs.released}</td>
                    <td>{rs.acquiredBy === co.id ? <span className="good">{t("Yours now")}</span> : rs.bust ? <span className="bad">{t("Bust")}</span> : active ? <span className="good">{t("Active")}</span> : <span className="muted">{t("Opens {year}", { year: r.from ?? "" })}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {tab === "news" && (
          <div className="stack">
            {[...game.market.news].reverse().slice(0, 40).map((n, i) => {
              const d = dateOf(n.week);
              return (
                <div key={i} className="row" style={{ alignItems: "flex-start" }}>
                  <span className="muted num" style={{ width: 92, flex: "none" }}>{t(d.season)} {d.year}</span>
                  <span>{txLine(n.text)}</span>
                </div>
              );
            })}
          </div>
        )}
        {tab === "awards" && (
          <div className="stack" data-testid="awards">
            {(game.market.awards ?? []).length === 0 && <p className="muted">{t("The first Dicefall Con is in summer 1980.")}</p>}
            {[...(game.market.awards ?? [])].reverse().slice(0, 40).map((a, i) => {
              const mine = a.publisher === me(game)?.id;
              const award = C.award[a.award]?.name;
              const con = C.convention[a.con]?.name;
              return (
                <div key={i} className="card spread" style={{ padding: "6px 12px", borderLeft: mine ? "4px solid var(--gold)" : undefined }}>
                  <span><strong>{award ? t(award) : award}</strong> · {a.name} <span className="muted">({game.companies.find((c) => c.id === a.publisher)?.name ?? C.rival[a.publisher]?.name})</span></span>
                  <span className="muted">{con ? t(con) : con} {dateOf(a.week).year}</span>
                </div>
              );
            })}
          </div>
        )}
        {tab === "notebook" && <Notebook />}
      </div>
    </Sheet>
  );
}

function Notebook() {
  const game = useGame((s) => s.game)!;
  const co = me(game)!;
  const entries = Object.entries(co.notebook).filter(([k]) => k.startsWith("mt:")).map(([k, v]) => {
    const [, m, th] = k.split(":");
    return { m, t: th, v };
  }).sort((a, b) => b.v - a.v);
  const total = Object.keys(C.mechanic).length * Object.keys(C.theme).length;
  return (
    <div className="stack">
      <div className="row" style={{ gap: 8 }} title={t("Combos you know: {n} of {total} · secret perfect combos: {found} of 24", { n: entries.length, total: total.toLocaleString("en-US"), found: co.stats.combosFound })}>
        <span className="biz-chip"><Icon name="doc" size={14} /><span>{entries.length}<span className="muted" style={{ fontWeight: 400 }}>/{total.toLocaleString("en-US")}</span></span></span>
        <span className="biz-chip"><Icon name="star" size={14} color="var(--gold)" /><span>{co.stats.combosFound}<span className="muted" style={{ fontWeight: 400 }}>/24</span></span></span>
      </div>
      <table className="list biz-compact">
        <thead><tr><th>{t("Mechanic")}</th><th>{t("Theme")}</th><th>{t("Fit")}</th><Th icon="calendar" label={t("This year")} /></tr></thead>
        <tbody>
          {entries.slice(0, 60).map((e) => (
            <tr key={e.m + e.t}>
              <td>{t(C.mechanic[e.m].name)}</td>
              <td>{t(C.theme[e.t].name)}</td>
              <td className="num">{"●".repeat(e.v)}<span style={{ opacity: 0.25 }}>{"●".repeat(5 - e.v)}</span></td>
              <td className="muted" style={{ fontSize: "0.82rem" }}>{(() => {
                const n = themeReleasesLastYear(game, e.t);
                const hot = heat(game, "theme", e.t) >= 0.25;
                const crowded = saturation(game, e.t) > 0;
                return <span className="biz-line" title={`${tp(n, "{n} game", "{n} games")}${hot ? ` · ${t("trending")}` : ""}${crowded ? ` · ${t("crowded")}` : ""}`}><span className="biz-ic num"><Icon name="box" size={13} />{n}</span>{hot && <Icon name="trend" size={14} color="var(--coral)" />}{crowded && <Icon name="warn" size={14} color="var(--warn)" />}</span>;
              })()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
